var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/*
 * Copyright (c) 2019-present Sonatype, Inc.
 * This program and the accompanying materials are made available under
 * the terms of the Eclipse Public License 2.0 which accompanies this
 * distribution and is available at https://www.eclipse.org/legal/epl-2.0/.
 */
import React, { useRef } from 'react';
import classnames from 'classnames';
import { partial } from 'ramda';
import './NxSearchDropdown.scss';
import forwardRef from '../../util/genericForwardRef';
import { propTypes } from './types';
import NxFilterInput from '../NxFilterInput/NxFilterInput';
import NxDropdownMenu from '../NxDropdownMenu/NxDropdownMenu';
import NxLoadWrapper from '../NxLoadWrapper/NxLoadWrapper';
export var SEARCH_DEBOUNCE_TIME = 500;
function NxSearchDropdownRender(props, ref) {
    var classNameProp = props.className, loading = props.loading, error = props.error, matches = props.matches, onSelect = props.onSelect, searchText = props.searchText, onSearchTextChange = props.onSearchTextChange, onSearch = props.onSearch, long = props.long, disabled = props.disabled, emptyMessage = props.emptyMessage, attrs = __rest(props, ["className", "loading", "error", "matches", "onSelect", "searchText", "onSearchTextChange", "onSearch", "long", "disabled", "emptyMessage"]), menuRef = useRef(null), filterRef = useRef(null), className = classnames('nx-search-dropdown', classNameProp), filterClassName = classnames('nx-search-dropdown__input', { 'nx-text-input--long': long }), menuClassName = classnames('nx-search-dropdown__menu', {
        'nx-search-dropdown__menu--error': !!error
    });
    /*
     * When the dropdown is closed while focus is within it, set focus back to the text input. Otherwise
     * it goes back to the <body> which is less helpful especially when within a modal. Note that we also use
     * a distinct react `key` on the dropdown when it is in error state to get it to re-render entirely when
     * switching to and from that state - thereby triggering this logic when the error state is cleared which would
     * result in the Retry button (which may have focus) being removed from DOM
     */
    function onMenuClosing() {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        var focusedEl = document.activeElement, menuEl = menuRef.current;
        if (menuEl && menuEl.contains(focusedEl) && filterRef.current) {
            var inputEl = filterRef.current.querySelector(':scope input');
            inputEl.focus();
        }
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
    }
    // There is a requirement that when there is an error querying the data, if the user navigates away from
    // the component and then comes back to it the search should be retried automatically
    function handleComponentFocus(evt) {
        // check if this is focus coming into the component from somewhere else on the page, not just moving between
        // children of this component and not from focus coming into the browser from some other window
        var comingFromOutsidePage = evt.relatedTarget === null, comingFromChildNode = evt.relatedTarget instanceof Node && evt.currentTarget.contains(evt.relatedTarget);
        if (!(comingFromOutsidePage || comingFromChildNode) && error) {
            doSearch(searchText);
        }
    }
    function handleFilterChange(value) {
        onSearchTextChange(value);
        if (value.trim() !== searchText.trim()) {
            doSearch(value);
        }
    }
    function doSearch(value) {
        onSearch(value.trim());
    }
    var handleKeyDown = function (event) {
        if (event.key === 'Escape') {
            onSearchTextChange('');
            event.preventDefault();
        }
    };
    return (React.createElement("div", __assign({ ref: ref, className: className, onFocus: handleComponentFocus }, attrs),
        React.createElement(NxFilterInput, { ref: filterRef, className: filterClassName, value: searchText, onChange: handleFilterChange, disabled: disabled || undefined, placeholder: "Search", searchIcon: true, onKeyDown: handleKeyDown }),
        searchText && !disabled &&
            React.createElement(NxDropdownMenu, { key: error ? 'error' : 'no-error', ref: menuRef, className: menuClassName, onClosing: onMenuClosing, onKeyDown: handleKeyDown },
                React.createElement(NxLoadWrapper, __assign({}, { loading: loading, error: error }, { retryHandler: function () { return doSearch(searchText); } }), matches.length ? matches.map(function (match) {
                    return React.createElement("button", { className: "nx-dropdown-button", key: match.id, onClick: partial(onSelect, [match]) }, match.displayName);
                }) :
                    React.createElement("div", { className: "nx-search-dropdown__empty-message" }, emptyMessage || 'No Results Found')))));
}
var NxSearchDropdown = Object.assign(forwardRef(NxSearchDropdownRender), { propTypes: propTypes });
export default NxSearchDropdown;
//# sourceMappingURL=NxSearchDropdown.js.map